import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {catchError, concatMap, filter, map, switchMap, tap} from 'rxjs/operators';
import * as UserAuthActions from './userAuth.actions';
import { TokenStorageService } from '@app/core/services/token-storage.service';
import { Router } from '@angular/router';
import { ToastService } from '@app/core/services/toast.service';
import { Store } from '@ngrx/store';
import { AppState } from '../app.state';
import { UserRoles } from '@app/core/enums/userRoles.enum';
import { Response } from '@app/core/interfaces/response';
import { UserAuthService } from '@app/features/login/services/userAuth.service';
import { AutoLogoutService } from '@app/core/services/auto-logout.service';
import { LogoutType } from '@app/core/enums/logout-types.enum';
import { PostAuthCheckFailureReasons } from '@app/features/login/enums/postAuthCheckFailureReasons.enum';
import {
  DefaultRouteRequested,
  ImpersonateSuccess,
  LogoutOnAlternateTab,
  StopImpersonation,
  EffectiveUserChanged, ImpersonationRequested
} from "./userAuth.actions";
import {AdminService} from "@app/features/adminDashboard/services/admin.service";
import { signOut } from 'aws-amplify/auth';
import {EMPTY, of} from "rxjs";
import {AuthResponse} from "@app/features/login/interfaces/authResponse.interface";
import {User} from "@app/core/interfaces";
import { MatDialog } from "@angular/material/dialog";

@Injectable()
export class UserAuthEffects {
  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private tokenStorageService: TokenStorageService,
    private router: Router,
    private toastService: ToastService,
    private userAuthService: UserAuthService,
    private adminService: AdminService,
    private autoLogoutService: AutoLogoutService,
    private dialog: MatDialog
  ) { }

  impersonate$ =  createEffect(() =>
      this.actions$.pipe(
        ofType(ImpersonateSuccess),
        tap((action) => {
          this.tokenStorageService.setImpersonating(action.response.user);
        }),
        concatMap((action) => {
          const user = this.tokenStorageService.getEffectiveUser();
          return [
            EffectiveUserChanged({ user }),
            DefaultRouteRequested({ user })
          ]
        })
      ));

  stopImpersonation$ =  createEffect(() =>
      this.actions$.pipe(ofType(StopImpersonation),
        tap(() => {
          this.tokenStorageService.setImpersonating(null);
        }),
        concatMap((action) => {
          const user = this.tokenStorageService.getEffectiveUser();
          return [
            EffectiveUserChanged({ user }),
            DefaultRouteRequested({ user })
          ];
        })
      ));

  loginSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserAuthActions.LoginSuccess),
        tap((action) => {
            this.tokenStorageService.saveUser(action.response.user);
            // Start auto-logout timer based on inactivity
            this.autoLogoutService.startWatching(() => {
              this.autoLogoutService.stopWatching();
              this.store.dispatch(UserAuthActions.SessionExpired({ logoutType: LogoutType.INACTIVITY }));
            });
        }),
        concatMap((action) => {
          const user = this.tokenStorageService.getEffectiveUser();
          const actions = [];
          actions.push(EffectiveUserChanged({user}));
          actions.push(DefaultRouteRequested({
              user,
              returnUrl: sessionStorage.getItem('returnUrl') ?? undefined
            }));
          if(this.tokenStorageService.isImpersonating())
          {
            actions.push(ImpersonationRequested({impersonatedUserId: this.tokenStorageService.getImpersonatedUser()!.id}));
          }
          return actions;
        })
        )
      );

    loginFailure$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(UserAuthActions.LoginFailure),
                tap(async (action) => {
                  this.tokenStorageService.clearStorage();
                  await signOut();
                  switch(action.error) {
                    case PostAuthCheckFailureReasons.PASSWORD_EXPIRED:
                      // Using query params instead of navigation state to pass userName since state does not retain on page refresh
                      this.router.navigate(['/expired-password'], { queryParams: { identifier: action.data ? action.data[0] : '' } });
                      break;
                    case PostAuthCheckFailureReasons.NEW_PASSWORD_REQUIRED:
                      const userName = action.data && action.data[0] ? action.data[0] : '';
                      const session = action.data && action.data[1] ? action.data[1] : '';
                      // Using query params instead of navigation state to pass userName since state does not retain on page refresh
                      this.router.navigate(['/force-change-password'], { queryParams: { userName, session } });
                      break;
                    default:
                      this.toastService.showErrorToast(action.error);
                      break;
                  }
                })
            ),
            {
                dispatch: false
            }
    );

    resetPasswordFailure$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(UserAuthActions.ResetPasswordFailure),
                tap((action) => {
                    this.toastService.showErrorToast(action.error);
                    this.tokenStorageService.clearStorage();
                })
            ),
            {
                dispatch: false
            }
    );

    resetPasswordSuccess$ = createEffect(
      () =>
        this.actions$.pipe(
          ofType(UserAuthActions.ResetPasswordSuccess),
          tap((action) => {
            this.toastService.showSuccessToast(action.response);
            this.tokenStorageService.clearStorage();
          })
        ),
      {
        dispatch: false
      }
    );

    logoutSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(UserAuthActions.LogoutSuccess, UserAuthActions.SessionExpired),
                tap(async (action) => {
                    this.autoLogoutService.stopWatching();
                    this.autoLogoutService.clearLastActivity();
                    this.tokenStorageService.clearStorage();
                    await signOut();
                    localStorage.setItem('logoutType', action.logoutType);  // Store logout type to show appropriate banner after navigation. Navigation state does not work here for some reason
                    localStorage.setItem('logout-time', Date.now().toString());
                    this.router.navigate(['/portal-login']);
                })
            ),
            {
                dispatch: false
            }
    );

    changePassword$ = createEffect(() =>
      this.actions$.pipe(
        ofType(UserAuthActions.ChangePasswordRequested),
        switchMap((action) =>
          this.userAuthService.changePassword({
            accessToken: action.accessToken,
            oldPassword: action.oldPassword,
            newPassword: action.newPassword,
            confirmPassword: action.confirmPassword
          }).pipe(
            map((response: Response) => {
                return response.succeeded
                  ? UserAuthActions.ChangePasswordSuccess({ response: 'Your password has been updated. Please log in again with your new password.' })
                  : UserAuthActions.ChangePasswordFailure({ error: 'Password change failed. Check your password and try again.' });
              }
            )
          )
        )
      )
    );

    changePasswordSuccess$ = createEffect(() =>
      this.actions$.pipe(
        ofType(UserAuthActions.ChangePasswordSuccess),
        tap((action) => {
          this.toastService.showSuccessToast(action.response);
        }),
        map(() => UserAuthActions.LogoutRequested({ logoutType: LogoutType.ON_PASSWORD_CHANGE }))
      )
    );

    changePasswordFailure$ = createEffect(
      () =>
        this.actions$.pipe(
          ofType(UserAuthActions.ChangePasswordFailure),
          tap((action) => {
            this.toastService.showErrorToast(action.error);
          })
        ),
      { dispatch: false }
    );

    logout$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(UserAuthActions.LogoutRequested),
                switchMap((action) =>
                    this.userAuthService.logoutUser()
                        .pipe(
                            map((response: Response) =>
                                response.succeeded
                                    ? UserAuthActions.LogoutSuccess({ logoutType: action.logoutType })
                                    : UserAuthActions.LogoutFailure({
                                        error: response.errorMessages.join(', ')
                                    }))
                        )
                    )
            )
    );

    logoutFailure$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(UserAuthActions.LogoutFailure),
                tap((action) => {
                  this.toastService.showErrorToast(action.error);
                })
          ),
        {
          dispatch: false
        }
    );

    logoutOnAlternateTab$ = createEffect(
      () =>
        this.actions$.pipe(
          ofType(UserAuthActions.LogoutOnAlternateTab),
          tap(async () => {
            this.autoLogoutService.stopWatching();
            this.autoLogoutService.clearLastActivity();
            this.tokenStorageService.clearStorage();
            this.dialog.closeAll();
            await signOut();
            this.router.navigate(['/portal-login']);
          })
        ),
      {
        dispatch: false
      }
    );

    defaultRouteRequested$ = createEffect(
      () => this.actions$.pipe(
        ofType(UserAuthActions.DefaultRouteRequested),
        tap((props) => {
          const returnUrl = props.returnUrl ?? sessionStorage.getItem('returnUrl');
          if (returnUrl) {
            sessionStorage.removeItem('returnUrl');
            this.router.navigateByUrl(returnUrl);
          } else {
            if (props.user?.role === UserRoles.workCompUser) {
              this.router.navigate(['wc-dashboard']);
            } else if (props.user?.role === UserRoles.referralUser) {
              this.router.navigate(['ref-dashboard']);
            } else if (props.user?.role === UserRoles.adminUser) {
              this.router.navigate(['admin-dashboard']);
            } else {
              this.router.navigate(['portal-login']);
            }
          }
        })
      ),
      { dispatch: false }
    );

  impersonationRequested$ = createEffect(
    () => this.actions$.pipe(
      ofType(UserAuthActions.ImpersonationRequested),
      switchMap((props) =>
        this.adminService.impersonate$(props.impersonatedUserId).pipe(
          map((response) => {
            if(!response.succeeded) {
              this.tokenStorageService.setImpersonating(null);
              this.toastService.showErrorToast(response.errorMessage ?? "Impersonation Failed");
              return StopImpersonation({ user: this.tokenStorageService.getEffectiveUser() });
            }
            this.toastService.showSuccessToast("Impersonation successful");
            return ImpersonateSuccess({ response: response });
          }),
          catchError((err, caught) => {
            console.log(JSON.stringify(err));
            this.tokenStorageService.setImpersonating(null);
            this.toastService.showErrorToast("Impersonation Failed");
            return of(StopImpersonation({ user: this.tokenStorageService.getEffectiveUser() }));
          })
        )
      )
    )
  );
}
